import { capitalize, COLORS, stringSearch } from "@coeff/utils";
import { Tag, Tooltip } from "antd";
import React, { useState } from "react";
import styled from "styled-components";

import {
  Button,
  ConfirmBox,
  FlexColumn,
  FlexRow,
  Input,
  LoaderWithPerfTimings,
  Option,
  Paper,
  PaperContent,
  Select,
  Typography,
} from "../../../common";

const StyledSelect = styled(Select)`
  && {
    margin: 0px;
    width: 100%;

    .ant-select-selector {
      border-color: ${COLORS.black15}!important;
      overflow: hidden;
    }

    .ant-select-arrow {
      opacity: 1;
    }
  }

  &&.ant-select.ant-select-multiple {
    .ant-select-selector {
      padding-bottom: 4px !important;
    }

    .ant-select-selection-search-input {
      margin-top: 8px;
    }
  }
`;

const StyledTag = styled(Tag)`
  background: ${COLORS.coeblue};
  color: ${COLORS.black85};
  font-size: 14px;
  border-radius: 6px;
  padding: 4px 8px;
  border: transparent;
  margin: 4px 4px 0 0;
`;

const StyledSelectDropdown = styled.div`
  max-width: 270px;
  padding-top: 8px;

  && {
    .ant-select-item.ant-select-item-group {
      color: #989898;
    }

    .ant-select-item-option svg path {
      fill: #989898;
    }

    .ant-select-item-option.ant-select-item-option-selected svg path {
      fill: ${COLORS.coeblue4};
    }

    .ant-select-item {
      color: ${COLORS.black85};

      .ant-select-item-option-content {
        padding-right: 8px;
      }

      &.ant-select-item-option-selected {
        font-weight: 500;
        color: ${COLORS.coeblue4};
      }
    }
  }
`;
const StyledDropdownFooter = styled.div<{ disabled?: boolean }>`
  padding: 8px 12px;
  color: ${COLORS.black45};
  border-top: 1px solid ${COLORS.black9};

  ${({ disabled }) =>
    disabled
      ? `
      cursor: not-allowed;
      pointer-events: none;
      opacity: 0.5;
  `
      : ""}
`;

type Props = {
  alias?: string;
  dataSourceId: string | undefined;
  disabled: boolean;
  availableSchemas: string[];
  selectedSchemas: string[];
  setSelectedSchemas: (val: string[]) => void;
  schemaLoading: boolean;
  schemaError: unknown;
  reloadSchema: () => void;
};

export const LimitSchemasInput: React.FC<Props> = ({
  alias,
  dataSourceId,
  disabled,
  availableSchemas,
  selectedSchemas,
  setSelectedSchemas,
  schemaLoading,
  schemaError,
  reloadSchema,
}) => {
  const label = alias ?? "schema";

  const [isSchemaLimitModalVisible, setIsSchemaLimitModalVisible] = useState(false);

  return (
    <>
      <div>
        <FlexRow style={{ justifyContent: "space-between" }}>
          <span>{capitalize(label)} visiblity</span>

          {!disabled && selectedSchemas.length ? (
            <Button
              type="link"
              noPadding
              style={{ height: "min-content", fontWeight: 400, lineHeight: 1.3 }}
              onClick={() => setSelectedSchemas([])}
            >
              clear all
            </Button>
          ) : null}
        </FlexRow>

        {dataSourceId ? (
          <>
            <StyledSelect
              loading={schemaLoading && !disabled}
              disabled={schemaLoading || disabled}
              mode="multiple"
              virtual={true}
              size="middle"
              placeholder={`All ${label}s`}
              showSearch={true}
              filterOption={(input, option) => {
                return (
                  stringSearch(option?.title, input) || stringSearch(String(option?.value), input)
                );
              }}
              error={schemaError !== null}
              notFoundContent={
                schemaLoading ? (
                  <div style={{ height: "200px" }}>
                    <LoaderWithPerfTimings
                      name="SQLSchemaSelect"
                      size="small"
                      style={{
                        display: "block",
                        position: "absolute",
                        top: "45%",
                        left: "45%",
                      }}
                    />
                  </div>
                ) : null
              }
              onChange={(val: Array<string>) => {
                setSelectedSchemas(val);
              }}
              value={schemaLoading && !disabled ? [] : selectedSchemas}
              defaultValue={schemaLoading && !disabled ? [] : selectedSchemas}
              optionLabelProp="title"
              tagRender={props => (
                <StyledTag {...props} closable={!(schemaLoading || disabled)}>
                  {availableSchemas.some(val => val === props.value)
                    ? `#${props.label}`
                    : props.label}
                </StyledTag>
              )}
              dropdownRender={menu => (
                <StyledSelectDropdown>
                  {menu}

                  <StyledDropdownFooter disabled={schemaLoading} onClick={reloadSchema}>
                    <a style={{ color: COLORS.coeblue4 }}>Refresh list</a>
                  </StyledDropdownFooter>
                </StyledSelectDropdown>
              )}
              getPopupContainer={triggerNode => triggerNode.parentElement}
            >
              {availableSchemas.map(schema => (
                <Option key={schema} value={schema} title={schema}>
                  {schema}
                </Option>
              ))}
            </StyledSelect>

            <Typography fontSize={12} fontWeight={500} style={{ marginTop: 4 }}>
              <Typography color="textSecondary">All {label}s are shown by default.</Typography>

              <a onClick={() => setIsSchemaLimitModalVisible(true)}>Learn more</a>
            </Typography>
          </>
        ) : (
          <Tooltip title={`You can select ${label}s after you connect your database.`}>
            <div>
              <Input size="middle" placeholder={`All ${label}s`} disabled={true} />
            </div>
          </Tooltip>
        )}
      </div>

      <ConfirmBox
        closable
        reverseButtonOrder
        submitText="Got it"
        showSubmit
        submitDisplayType="button"
        submitButtonProps={{ type: "link" }}
        title={`${capitalize(label)} visibility`}
        visible={isSchemaLimitModalVisible}
        showCancel={false}
        onCancel={() => setIsSchemaLimitModalVisible(false)}
        onSubmit={() => setIsSchemaLimitModalVisible(false)}
      >
        <FlexColumn style={{ lineHeight: 1.3, fontSize: 14 }} gap={2}>
          <Typography color="textSecondary">
            By default, all {label}s are shown when creating an import. If your database contains a
            large number of {label}s, you can reduce load times by displaying a subset of {label}s.
          </Typography>

          <Paper variant="warning">
            <PaperContent size="small">
              <Typography fontWeight={500}>
                <b>Note: </b>Hiding {label}s does not prevent users from querying tables or views.
                To block access, use your SQL database's built-in user access controls.
              </Typography>
            </PaperContent>
          </Paper>
        </FlexColumn>
      </ConfirmBox>
    </>
  );
};
